/**
 * FP3-051 / FP3-063 — Solo provider / org-admin schools management card.
 *
 * Renders:
 *   • the org's schools (active by default; archived ones show
 *     behind a "Show archived" toggle)
 *   • inline rename
 *   • inline Add school form
 *   • "Create Primary Workspace" repair button when zero schools
 *   • Archive / Restore row actions (FP3-063)
 *   • Delete row action when the school has no dependents (FP3-063);
 *     server returns 409 with a friendly message otherwise
 *
 * Permissions are server-enforced. The FE just exposes the controls
 * to roles that can use them (admin family + solo provider owner)
 * and asks the server to refuse anything else.
 */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus, PencilSimple, FloppyDisk, X, Archive, ArrowCounterClockwise, Trash,
} from "@phosphor-icons/react";
import axios from "axios";

import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

function dependentsBlurb(d) {
  if (!d || !Object.keys(d).length) return "";
  const parts = [];
  if (d.students) parts.push(`${d.students} student${d.students === 1 ? "" : "s"}`);
  if (d.schedule_rules) parts.push(`${d.schedule_rules} schedule rule${d.schedule_rules === 1 ? "" : "s"}`);
  if (d.sessions) parts.push(`${d.sessions} session${d.sessions === 1 ? "" : "s"}`);
  if (d.constraints) parts.push(`${d.constraints} constraint${d.constraints === 1 ? "" : "s"}`);
  return parts.join(", ");
}

export default function ManageSchoolsCard({
  organizationId,
  schools,
  onChange,
}) {
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState("");
  const [editingName, setEditingName] = useState("");
  const [busy, setBusy] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // {id, name, dependents?}

  const trigger = (k) => onChange?.(k);

  const visibleSchools = useMemo(() => {
    const list = schools || [];
    return showArchived ? list : list.filter((s) => !s.is_archived);
  }, [schools, showArchived]);
  const archivedCount = useMemo(
    () => (schools || []).filter((s) => s.is_archived).length,
    [schools],
  );

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.createSchool({
        name,
        organization_id: organizationId,
        access_type: "inherited",
        membership_status: "inherited",
        billing_interval: "inherited",
      });
      toast.success(`Added "${name}"`);
      setNewName("");
      setAdding(false);
      trigger();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not add school");
    } finally {
      setBusy(false);
    }
  };

  const handleSaveName = async (sid) => {
    const name = editingName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.patchSchool(sid, { name });
      toast.success("School name updated");
      setEditingId("");
      setEditingName("");
      trigger();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not rename school");
    } finally {
      setBusy(false);
    }
  };

  const handleEnsurePrimary = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API_URL}/solo/workspace/ensure-primary`,
        {},
      );
      if (r.data?.noop) {
        toast.info("Primary Workspace already exists");
      } else {
        toast.success(`Created "${r.data?.school?.name || "Primary Workspace"}"`);
      }
      trigger();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not create Primary Workspace");
    } finally {
      setBusy(false);
    }
  };

  const handleArchive = async (s) => {
    setBusy(true);
    try {
      await api.archiveSchool(s.id);
      toast.success(`Archived "${s.name}"`);
      trigger();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not archive campus");
    } finally {
      setBusy(false);
    }
  };

  const handleRestore = async (s) => {
    setBusy(true);
    try {
      await api.restoreSchool(s.id);
      toast.success(`Restored "${s.name}"`);
      trigger();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not restore campus");
    } finally {
      setBusy(false);
    }
  };

  const openDeleteConfirm = async (s) => {
    setBusy(true);
    try {
      const r = await api.getSchoolDependents(s.id);
      const deps = r?.data || {};
      setConfirmDelete({
        id: s.id, name: s.name || "Unnamed campus", dependents: deps,
      });
    } catch (e) {
      // Surface server error but still open the dialog with no
      // dependents — the DELETE call will refuse if anything is
      // attached.
      setConfirmDelete({ id: s.id, name: s.name || "Unnamed campus", dependents: null });
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!confirmDelete) return;
    setBusy(true);
    try {
      await api.deleteSchool(confirmDelete.id);
      toast.success(`Deleted "${confirmDelete.name}"`);
      setConfirmDelete(null);
      trigger();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "school_has_dependents") {
        const blurb = dependentsBlurb(d.dependents);
        toast.error(
          blurb
            ? `Can't delete — still linked to ${blurb}. Archive it instead.`
            : (d.message || "Cannot delete this campus."),
        );
      } else {
        toast.error(typeof d === "string" ? d : "Could not delete campus");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="workspace-schools-manage" className="space-y-3">
      {schools.length === 0 && (
        <div className="rounded-md bg-[#F7F5EF] border border-[#E5E3DB] p-3">
          <p className="text-[12px] text-[#4B5563] mb-2">
            No schools yet. Create your "Primary Workspace" to enable
            schedule rules, calendar exceptions, and the schedule builder.
          </p>
          <Button
            data-testid="workspace-ensure-primary-btn"
            size="sm"
            onClick={handleEnsurePrimary}
            disabled={busy}
            className="bg-[#1E3F33] hover:bg-[#173127] text-white"
          >
            <Plus size={12} weight="bold" className="mr-1.5" />
            Create Primary Workspace
          </Button>
        </div>
      )}

      {schools.length > 0 && (
        <ul className="space-y-2">
          {visibleSchools.map((s) => {
            const isEditing = editingId === s.id;
            const archived = !!s.is_archived;
            return (
              <li
                key={s.id}
                data-testid={`workspace-school-manage-${s.id}`}
                data-archived={archived ? "true" : "false"}
                className={`flex items-center gap-2 py-1.5 border-b border-[#F1F2F4] last:border-0 ${
                  archived ? "opacity-70" : ""
                }`}
              >
                {isEditing ? (
                  <>
                    <Input
                      data-testid={`workspace-school-edit-input-${s.id}`}
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      autoFocus
                      className="h-8 text-[13px]"
                    />
                    <Button
                      data-testid={`workspace-school-save-${s.id}`}
                      size="sm"
                      variant="ghost"
                      onClick={() => handleSaveName(s.id)}
                      disabled={busy || !editingName.trim()}
                      className="h-8 px-2"
                    >
                      <FloppyDisk size={14} weight="duotone" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditingId("");
                        setEditingName("");
                      }}
                      className="h-8 px-2"
                    >
                      <X size={14} />
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] text-[#1F2937] font-medium truncate flex items-center gap-2">
                        <span>{s.name || "Unnamed campus"}</span>
                        {archived && (
                          <span
                            data-testid={`workspace-school-archived-badge-${s.id}`}
                            className="text-[10px] uppercase tracking-[0.08em] font-semibold rounded-full px-2 py-0.5 bg-[#F3F1EC] text-[#6B7280]"
                          >
                            Archived
                          </span>
                        )}
                      </div>
                    </div>
                    {!archived && (
                      <Button
                        data-testid={`workspace-school-edit-${s.id}`}
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditingId(s.id);
                          setEditingName(s.name || "");
                        }}
                        className="h-8 px-2 text-[#6B7280] hover:text-[#1E3F33]"
                        title="Rename"
                      >
                        <PencilSimple size={13} weight="duotone" />
                      </Button>
                    )}
                    {!archived ? (
                      <>
                        <Button
                          data-testid={`workspace-school-archive-${s.id}`}
                          size="sm"
                          variant="ghost"
                          onClick={() => handleArchive(s)}
                          disabled={busy}
                          className="h-8 px-2 text-[#6B7280] hover:text-[#7A5B1F]"
                          title="Archive campus"
                        >
                          <Archive size={13} weight="duotone" />
                        </Button>
                        <Button
                          data-testid={`workspace-school-delete-${s.id}`}
                          size="sm"
                          variant="ghost"
                          onClick={() => openDeleteConfirm(s)}
                          disabled={busy}
                          className="h-8 px-2 text-[#6B7280] hover:text-[#7A3A2C]"
                          title="Delete campus (only when empty)"
                        >
                          <Trash size={13} weight="duotone" />
                        </Button>
                      </>
                    ) : (
                      <Button
                        data-testid={`workspace-school-restore-${s.id}`}
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRestore(s)}
                        disabled={busy}
                        className="h-8 px-2 text-[#6B7280] hover:text-[#1E3F33]"
                        title="Restore campus"
                      >
                        <ArrowCounterClockwise size={13} weight="duotone" />
                      </Button>
                    )}
                  </>
                )}
              </li>
            );
          })}
          {visibleSchools.length === 0 && !showArchived && schools.length > 0 && (
            <li
              data-testid="workspace-schools-all-archived"
              className="text-[12px] text-[#6B7280] italic px-1"
            >
              All campuses are archived. Toggle "Show archived" below to
              restore one.
            </li>
          )}
        </ul>
      )}

      {archivedCount > 0 && (
        <label
          className="flex items-center gap-2 text-[12px] text-[#3F4756] cursor-pointer pt-1"
          data-testid="workspace-schools-show-archived-label"
        >
          <Checkbox
            data-testid="workspace-schools-show-archived"
            checked={showArchived}
            onCheckedChange={(c) => setShowArchived(!!c)}
            className="border-[#D4D2CC]"
          />
          Show archived ({archivedCount})
        </label>
      )}

      {adding ? (
        <div className="flex items-center gap-2 pt-1">
          <Input
            data-testid="workspace-school-new-name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="School / campus name"
            autoFocus
            className="h-8 text-[13px]"
          />
          <Button
            data-testid="workspace-school-add-confirm"
            size="sm"
            onClick={handleAdd}
            disabled={busy || !newName.trim()}
            className="h-8 bg-[#1E3F33] hover:bg-[#173127] text-white"
          >
            Add
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setAdding(false);
              setNewName("");
            }}
            className="h-8 px-2"
          >
            <X size={13} />
          </Button>
        </div>
      ) : (
        <Button
          data-testid="workspace-school-add-btn"
          size="sm"
          variant="outline"
          onClick={() => setAdding(true)}
          className="h-8 border-[#E5E3DB] text-[#1E3F33] hover:bg-[#E1EAE4]"
        >
          <Plus size={12} weight="bold" className="mr-1.5" />
          Add school / campus
        </Button>
      )}

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent data-testid="workspace-school-delete-dialog" className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this campus?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.dependents
                && (confirmDelete.dependents.students
                  || confirmDelete.dependents.schedule_rules
                  || confirmDelete.dependents.sessions
                  || confirmDelete.dependents.constraints) ? (
                <>
                  <strong>"{confirmDelete.name}"</strong> still has{" "}
                  {dependentsBlurb(confirmDelete.dependents)} attached. You
                  can&apos;t hard-delete it. Use Archive instead — it hides the
                  campus from active pickers without touching any student or
                  schedule data.
                </>
              ) : (
                <>
                  <strong>"{confirmDelete?.name}"</strong> has no students,
                  schedule rules, or sessions attached. Deleting is permanent
                  — this cannot be undone. Archive is reversible if you want
                  to keep the option open.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="workspace-school-delete-cancel" disabled={busy}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="workspace-school-delete-confirm"
              disabled={
                busy
                || !!(
                  confirmDelete?.dependents
                  && (confirmDelete.dependents.students
                    || confirmDelete.dependents.schedule_rules
                    || confirmDelete.dependents.sessions
                    || confirmDelete.dependents.constraints)
                )
              }
              onClick={handleDeleteConfirmed}
              className="bg-[#7A3A2C] hover:bg-[#5F2C22] text-white"
            >
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
